// useArtworkSeparations — resolve REAL per-artwork ink separations (pulled from
// Cloudflow via the cached per-job endpoint) for a set of jobs, so board bars
// show the actual inks/spots/coatings instead of a placeholder CMYK strip.
//
// LOAD DISCIPLINE (important): wave.abaris.com.au is a live production Cloudflow.
// To avoid bursting it while we MVP:
//   1. Every extraction is funnelled through a concurrency-1 queue below, so at
//      most ONE request is ever in flight — a slow trickle, never a smash.
//   2. Deduped by customer_ref (the artwork identity): a PDF shared by N jobs is
//      fetched once, not N times.
//   3. The backend caches each PDF's colorants (production.separation_extractions),
//      so a given artwork is pulled from Cloudflow once, EVER; later hits are
//      Postgres reads. staleTime:Infinity means we don't refetch in-session.
// Callers decide WHICH jobs to resolve and HOW: scheduled bars are full refs
// (bulk + trickle on miss); the Ready-to-Run pool rides along as `bulkOnly`
// refs that read the cache via the bulk request but never join the trickle.
//
// BULK-FIRST (2026-09-03): most of those resolutions are cache hits that never
// needed the queue — so one POST /jobs/separations/bulk (cache-only on the
// server, ~200 ms for a 62-job batch) answers them all, and only the genuine
// unknowns it reports as `pending` pay a real trickle fetch. Three lessons
// from the first, reverted wiring (1c1a754/b61afdf) are load-bearing here:
//   1. No query key derived from the ref list — it churns as each press's
//      schedule lands, which minted ~4 overlapping bulk requests per load.
//      The bulk is fired imperatively, debounced, and only for NEW refs.
//   2. The trickle is NEVER gated on the bulk. Every ref still goes through
//      serial() from mount; when its turn comes it first checks whether the
//      bulk already answered it and skips the network if so. A slow or failed
//      bulk degrades to exactly the old behaviour, not a blank board.
//   3. Superseded/unmounted bulk requests are aborted (AbortController),
//      with a hard timeout so a hung request can't linger.
import { useEffect, useMemo, useRef } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { getJobSeparations, getJobSeparationsBulk } from '../productionApi';
import {
  RawSeparation, ResolvedJobSeparations, SeparationRole, ArtworkStatus,
} from '../productionTypes';
import { GanttSep } from './ganttTypes';

// --- fetch lanes -------------------------------------------------------------
// `makeLane(n)` is a concurrency-limited queue: at most n calls in flight, the
// rest wait their turn. A rejection frees its slot like any other settle, so one
// bad artwork can't wedge the lane.
//
// There are three lanes rather than one shared tail, because the work is not all
// the same shape and lumping it together made the cheap thing wait on the dear one
// (2026-09-18: on ink-assign, every preview queued behind ~2.7s separation
// extractions, so a page of 130 SKUs took minutes to show pictures that were
// already cached).
//
// Cloudflow's documented ceiling is 2 req/s. An extraction takes ~2.7s, so N in
// flight is only N/2.7 req/s — concurrency 3 is ~1.1 req/s, comfortably under.
// The real cap is the backend's worker count: the plan is Basic B3 (4 cores) but
// FUNCTIONS_WORKER_PROCESS_COUNT is 3, so 3 requests can genuinely run at once
// and a 4th just queues behind them. Raise that setting and these can go up.
function makeLane(limit: number) {
  let active = 0;
  const waiting: Array<() => void> = [];
  const release = () => {
    active -= 1;
    waiting.shift()?.();
  };
  return function lane<T>(fn: () => Promise<T>): Promise<T> {
    const start = async (): Promise<T> => {
      active += 1;
      try {
        return await fn();
      } finally {
        release();
      }
    };
    if (active < limit) return start();
    return new Promise<T>((resolve, reject) => {
      waiting.push(() => { start().then(resolve, reject); });
    });
  };
}

/** Strictly one-at-a-time. The prepress gate and the ink order/balance views
 *  drive Cloudflow directly and deliberately keep that pace. */
export const serial = makeLane(1);

/** Artwork separation extraction — the expensive Cloudflow call (~2.7s). */
export const sepLane = makeLane(3);

/** Preview thumbnails. Mostly a cache hit (a DB read, no Cloudflow at all), so
 *  these have no business waiting behind extractions. */
export const thumbLane = makeLane(4);

// Fallback swatch when Cloudflow gives no preview colour for a separation.
const ROLE_FALLBACK_HEX: Record<SeparationRole, string> = {
  process: '#8a8a8a',
  spot: '#6c5cff',
  coating: '#e3ddcf',
  special_ink: '#c8c8c8',
  technical: '#5a5a5a',
  ignored: '#d0d0d0',
};

// Short badge for the hover legend: CMYK by first word, PANTONE number when
// present, else the first letters of the name.
function sepShort(name: string, role: SeparationRole): string {
  const n = (name || '').trim();
  const l = n.toLowerCase();
  if (l.startsWith('cyan')) return 'C';
  if (l.startsWith('magenta')) return 'M';
  if (l.startsWith('yellow')) return 'Y';
  // Artwork still calls it Black/Key/K — that's Cloudflow's vocabulary, so keep
  // accepting all of it. We just badge it 'B'.
  if (l.startsWith('black') || l === 'key' || l === 'k' || l === 'b') return 'B';
  const num = n.match(/\d{3,4}/); // 'PANTONE 2757 C' -> '2757'
  if (num) return num[0];
  const alpha = n.replace(/[^A-Za-z]/g, '').toUpperCase();
  return alpha.slice(0, 3) || (role === 'coating' ? 'VAR' : '?');
}

function toGanttSep(s: RawSeparation): GanttSep {
  return {
    short: sepShort(s.name, s.kind),
    hex: s.hex_colour || ROLE_FALLBACK_HEX[s.kind] || '#8a8a8a',
    role: s.kind,
    name: s.name,
  };
}

export interface ArtworkRef {
  jobNumber: string;
  customerRef: string | null | undefined;
  /**
   * Bulk-cache-only: this ref rides the bulk request (cache-only server-side,
   * never touches Cloudflow) but is NEVER trickled through the serial queue on
   * a miss. Used for the Ready-to-Run pool — ~250 backlog artworks would
   * otherwise churn the concurrency-1 queue against live Cloudflow for ages.
   * ~87% of the pool is already cached (measured 2026-09-03); the cold rest
   * shows "not read yet" and fills in as the Sunday warm timer catches up.
   * If the same ref also appears as a normal (scheduled) ref, trickle wins.
   */
  bulkOnly?: boolean;
}

// What the board needs per artwork: the ink strip (when resolved) plus enough
// status to show a "missing artwork" indicator and explain it on hover.
export interface ArtworkResolution {
  status: ArtworkStatus;       // resolved | not_found | error | no_path
  seps: GanttSep[];            // real inks when resolved, else []
  pdfPath: string | null;      // the cloudflow:// path we looked for
  error: string | null;        // Cloudflow's reason when not resolved
}

/**
 * Returns Map<customer_ref, ArtworkResolution>. A ref absent from the map
 * simply hasn't resolved yet (still queued in the concurrency-1 fetch) — the
 * caller treats that as "loading" and shows a placeholder. Never throws.
 */
// How long the ref set must hold still before a bulk fires. Press schedules
// land one query at a time, growing the ref set in quick bursts; debouncing
// coalesces a burst into one bulk instead of one per press.
const BULK_DEBOUNCE_MS = 300;
// A bulk that hasn't answered by now is aborted; its refs simply resolve via
// the trickle instead. Generous vs the ~400 ms it measures in production.
const BULK_TIMEOUT_MS = 15_000;
const BULK_MAX_JOBS = 250; // server-side _SEPARATIONS_BULK_MAX

export function useArtworkSeparations(refs: ArtworkRef[]): Map<string, ArtworkResolution> {
  const qc = useQueryClient();
  // Dedupe to one representative job per distinct artwork (customer_ref).
  // bulkOnly is AND-ed across occurrences: one scheduled (tricklable) job with
  // this artwork makes the whole ref tricklable, wherever else it appears.
  const distinct = useMemo(() => {
    const m = new Map<string, { jobNumber: string; bulkOnly: boolean }>();
    for (const r of refs) {
      const ref = (r.customerRef || '').trim();
      if (!ref || !r.jobNumber) continue;
      const prev = m.get(ref);
      if (!prev) m.set(ref, { jobNumber: r.jobNumber, bulkOnly: !!r.bulkOnly });
      else if (prev.bulkOnly && !r.bulkOnly) m.set(ref, { jobNumber: r.jobNumber, bulkOnly: false });
    }
    return Array.from(m.entries()); // [customer_ref, {jobNumber, bulkOnly}][]
  }, [refs]);

  // Bulk answers, keyed by customer_ref. Filled by the effect below; read by
  // each trickle fn at EXECUTION time (when its serial() turn comes), so a
  // bulk that lands while the queue is draining short-circuits every ref it
  // answered without any query-state gymnastics.
  const bulkAnswers = useRef(new Map<string, ResolvedJobSeparations>());
  // Refs already sent in some bulk request — each ref is asked at most once
  // per session, however often the ref set re-renders.
  const bulkSent = useRef(new Set<string>());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controllers = useRef(new Set<AbortController>());

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      // Only refs no earlier bulk has covered — schedule growth produces a
      // small delta batch, not a fresh full-board request.
      const batch = distinct.filter(([ref]) => !bulkSent.current.has(ref));
      if (!batch.length) return;
      batch.forEach(([ref]) => bulkSent.current.add(ref));

      const controller = new AbortController();
      controllers.current.add(controller);
      const timeout = setTimeout(() => controller.abort(), BULK_TIMEOUT_MS);

      (async () => {
        try {
          for (let i = 0; i < batch.length; i += BULK_MAX_JOBS) {
            const chunk = batch.slice(i, i + BULK_MAX_JOBS);
            const jobToRef = new Map(chunk.map(([ref, d]) => [d.jobNumber, ref]));
            const resp = await getJobSeparationsBulk(
              chunk.map(([, d]) => d.jobNumber), 'P15', controller.signal,
            );
            for (const [job, data] of Object.entries(resp.results || {})) {
              const ref = jobToRef.get(job);
              if (!ref) continue;
              bulkAnswers.current.set(ref, data);
              // Seed the per-ref query cache too. Trickle-enabled refs read
              // bulkAnswers at execution time anyway; this is how BULK-ONLY
              // refs (disabled queries — the Ready-to-Run pool) get their
              // answer surfaced: setQueryData fires their observers, so the
              // map below re-derives with the resolved seps.
              qc.setQueryData(['board-artwork-seps', ref], data);
            }
            // resp.pending: nothing to do — tricklable refs' serial fetches
            // find no bulk answer and do the real single fetch, as before;
            // bulk-only refs simply stay unresolved (shown as "not read yet").
          }
        } catch {
          // Failed/aborted bulk = the trickle resolves everything, which is
          // exactly the pre-bulk behaviour. Nothing to unwind.
        } finally {
          clearTimeout(timeout);
          controllers.current.delete(controller);
        }
      })();
    }, BULK_DEBOUNCE_MS);
  }, [distinct, qc]);

  // Unmount only: in-flight bulks are aborted. NOT on ref-set change — a
  // delta batch never supersedes an earlier one, so earlier requests stay
  // valid and useful.
  useEffect(() => {
    const active = controllers.current;
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      active.forEach((c) => c.abort());
    };
  }, []);

  const results = useQueries({
    queries: distinct.map(([ref, { jobNumber, bulkOnly }]) => ({
      // Dedicated key OUTSIDE the 'production' prefix: an artwork's colorants
      // don't change when a job is dragged, so we deliberately keep these
      // queries clear of productionKeys.all invalidations. Keyed by artwork
      // (customer_ref), press-independent, so it's fetched once per session.
      queryKey: ['board-artwork-seps', ref] as const,
      // profile=false: this hook reads only artwork_separations, artwork_status,
      // pdf_path and cloudflow_error (see the map below). Resolving the
      // separation profile is a query per card whose result we discard, ~67
      // times per board load.
      queryFn: () => serial(async () => {
        // The bulk usually lands (~400 ms) while this ref is still queued —
        // in which case this is a Map lookup, not a network call, and the
        // whole queue drains in one tick.
        const answered = bulkAnswers.current.get(ref);
        if (answered) return answered;
        return getJobSeparations(jobNumber, 'P15', false);
      }),
      // Bulk-only refs (the pool) never fetch: the query exists solely as a
      // subscription to the cache entry the bulk seeds via setQueryData. If
      // the ref later shows up scheduled, it flips tricklable and fetches.
      enabled: !bulkOnly,
      staleTime: Infinity,
      gcTime: Infinity,
      retry: 1,
    })),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sig = results.map((r) => r.dataUpdatedAt).join(',');
  return useMemo(() => {
    const map = new Map<string, ArtworkResolution>();
    distinct.forEach(([ref], i) => {
      const data = results[i]?.data;
      if (!data) return; // still loading — leave absent
      const seps = (data.artwork_separations || [])
        // Skip non-printing separations: 'ignored' + 'technical' (die lines,
        // crease/varnish-free masks, registration marks) — not real inks.
        .filter((s) => s.kind !== 'ignored' && s.kind !== 'technical')
        .map(toGanttSep);
      map.set(ref, {
        status: data.artwork_status,
        seps,
        pdfPath: data.pdf_path,
        error: data.cloudflow_error,
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distinct, sig]);
}
